import React, { type PropsWithChildren, useContext, useEffect, useRef } from "react";

export interface AdSlot {
  adSlotName: string;
  adSlotId: string;
  mapping: [number, number][][];
}

export interface AdConfigContextStore {
  networkCode?: string;
  adSlots?: AdSlot[];
  targeting?: Record<string, string[]>;
}

export const AdConfigContext = React.createContext<AdConfigContextStore | undefined>(undefined);

let resolveAdServicesEnabled: () => void;
let rejectAdServicesEnabled: (error: unknown) => void;
export const adServicesEnabled = new Promise<void>((resolve, reject) => {
  resolveAdServicesEnabled = resolve;
  rejectAdServicesEnabled = reject;
});

export interface PendingAd {
  getSlot: () => googletag.Slot | null;
  containerId: string;
}

const pendingAds: PendingAd[] = [];
let flushScheduled = false;
let firstBatchIssued = false;

/**
 * Queues an ad for display. All ads queued in the same tick are displayed and
 * fetched in ONE request so GAM can apply cross-slot rules (no duplicate
 * creatives, competitive exclusions). Later batches reuse the original page
 * correlator so per-page rules keep counting ads already shown.
 */
export const requestAdDisplay = (ad: PendingAd): void => {
  pendingAds.push(ad);
  if (flushScheduled) return;
  flushScheduled = true;
  adServicesEnabled
    .then(() => {
      setTimeout(() => {
        flushScheduled = false;
        const batch = pendingAds.splice(0, pendingAds.length);
        if (batch.length === 0) return;
        const gtag = (window.googletag = window.googletag ?? ({ cmd: [] } as unknown as typeof googletag));
        gtag.cmd.push(() => {
          const slots = batch
            .map((pending) => {
              gtag.display(pending.containerId);
              return pending.getSlot();
            })
            .filter((slot): slot is googletag.Slot => slot !== null);
          if (slots.length === 0) return;
          gtag.pubads().refresh(slots, { changeCorrelator: !firstBatchIssued });
          firstBatchIssued = true;
        });
      }, 0);
    })
    .catch((error: unknown) => {
      flushScheduled = false;
      console.error("requestAdDisplay: ad services failed to initialise", error);
    });
};

export interface AdConfigProviderProps extends PropsWithChildren {
  targeting?: Record<string, string[]>;
}

export const AdConfigProvider: React.FC<AdConfigProviderProps> = ({ children, targeting }) => {
  const context = useContext(AdConfigContext);
  const hasInitialised = useRef(false);

  if (globalThis.serverConfig === undefined && context === undefined) {
    console.warn("AdConfigProvider, no config is found. Context is not set. Noop");
  }

  useEffect(() => {
    if (hasInitialised.current) return;
    hasInitialised.current = true;

    const gtag = (window.googletag = window.googletag ?? ({ cmd: [] } as unknown as typeof googletag));
    gtag.cmd.push(() => {
      try {
        gtag.setConfig({
          collapseDiv: "ON_NO_FILL",
          singleRequest: true,
          disableInitialLoad: true,
          targeting: targeting,
        });
        gtag.enableServices();
        resolveAdServicesEnabled();
      } catch (error) {
        rejectAdServicesEnabled(error);
      }
    });
  }, []);

  const serverConfig = globalThis.serverConfig as AdConfigContextStore | undefined;

  if (serverConfig?.networkCode && serverConfig.adSlots) {
    const config: AdConfigContextStore = {
      networkCode: serverConfig.networkCode,
      adSlots: serverConfig.adSlots,
    };
    return <AdConfigContext.Provider value={config}>{children}</AdConfigContext.Provider>;
  } else {
    return <>{children}</>;
  }
};

export const useAdConfig = () => React.useContext(AdConfigContext);
